import { useState } from 'react'
import { downloadArchive, useArchives, useRequestArchive } from '../api/rooms'
import type { ArchiveResponse, RoomResponse } from '../api/types'
import { errorMessage, formatBytes, formatUtc } from '../lib/format'

/**
 * The close-out package of a Frozen or Archived room, for its owners: ask for one, watch
 * it build, download it with its hash. The server decides who may ask and refuses a room
 * that is still Open; this panel only appears where the server would say yes.
 */
export function CloseoutPanel({ room }: { room: RoomResponse }) {
  if (room.memberRole !== 'Owner' || (room.status !== 'Frozen' && room.status !== 'Archived')) {
    return null
  }
  return <CloseoutPanelBody roomId={room.id} roomStatus={room.status} />
}

export function CloseoutPanelBody({ roomId, roomStatus }: { roomId: string; roomStatus: string }) {
  const archives = useArchives(roomId)
  const request = useRequestArchive(roomId)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const pending = archives.data?.some((a) => a.status === 'Requested' || a.status === 'Building')

  return (
    <section className="panel closeout" aria-labelledby="closeout-title">
      <div className="row-between">
        <div>
          <h2 id="closeout-title" className="h2 no-margin">
            Close-out package
          </h2>
          <p className="muted small">
            Every document, the index, the members, the Q&amp;A log and the whole audit log as one
            zip with a SHA-256 you can verify. Built from the room as it is now ({roomStatus}).
          </p>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={request.isPending || pending}
          onClick={() => request.mutate()}
        >
          {pending ? 'Building…' : 'Build package'}
        </button>
      </div>
      {request.isError && (
        <p className="error small" role="alert">
          {errorMessage(request.error)}
        </p>
      )}
      {downloadError && (
        <p className="error small" role="alert">
          {downloadError}
        </p>
      )}
      {archives.data && archives.data.length > 0 && (
        <table className="table closeout-table">
          <thead>
            <tr>
              <th>Requested</th>
              <th>By</th>
              <th>Status</th>
              <th>Room was</th>
              <th>Size</th>
              <th>SHA-256</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {archives.data.map((a) => (
              <ArchiveRow
                key={a.id}
                archive={a}
                onDownload={() => {
                  setDownloadError(null)
                  downloadArchive(roomId, a.id).catch((e: unknown) =>
                    setDownloadError(errorMessage(e)),
                  )
                }}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function ArchiveRow({ archive, onDownload }: { archive: ArchiveResponse; onDownload: () => void }) {
  return (
    <tr>
      <td>{formatUtc(archive.requestedUtc)}</td>
      <td>{archive.requestedBy ?? ''}</td>
      <td>
        <span className={`status status-${archive.status.toLowerCase()}`}>{archive.status}</span>
        {archive.status === 'Failed' && archive.error && (
          <span className="small error-text"> {archive.error}</span>
        )}
      </td>
      <td>{archive.roomStatusAtBuild ?? ''}</td>
      <td>{archive.sizeBytes == null ? '' : formatBytes(archive.sizeBytes)}</td>
      <td className="mono small closeout-hash" title={archive.sha256 ?? ''}>
        {archive.sha256 ? `${archive.sha256.slice(0, 16)}…` : ''}
      </td>
      <td>
        {archive.status === 'Ready' && (
          <button type="button" className="btn btn-sm" onClick={onDownload}>
            Download
          </button>
        )}
      </td>
    </tr>
  )
}
